import { create } from 'zustand'

export interface NotificationItem {
  id: string
  eventKey: string
  title: string
  description: string
  conversationId: string | null
  campaignName: string | null
  isRead: boolean
  createdAt: string
  actorName: string | null
  referenceId?: string | null
  subject?: string | null
  assigneeName: string | null
  recipientName: string | null
  entityType?: string | null
}

interface NotificationsStoreState {
  notifications: NotificationItem[]
  unreadCount: number
  // Bumped only by addLive (never by setInitial) so UI code can distinguish
  // a live socket push from an initial/open-time list fetch without relying
  // on notifications-array identity, which also changes on setInitial.
  lastLiveId: string | null
  toastNotification: NotificationItem | null
  setInitial: (notifications: NotificationItem[], unreadCount: number) => void
  // Replaces the notifications list without touching unreadCount — the
  // unread badge is a tenant-wide count (from /notifications/unread-count),
  // not derived from whatever page of the list happens to be loaded.
  setNotifications: (notifications: NotificationItem[]) => void
  appendNotifications: (notifications: NotificationItem[]) => void
  addLive: (notification: NotificationItem) => void
  markRead: (id: string) => void
  markAllRead: () => void
  clearToast: () => void
}

export const useNotificationsStore = create<NotificationsStoreState>()((set) => ({
  notifications: [],
  unreadCount: 0,
  lastLiveId: null,
  toastNotification: null,
  setInitial: (notifications, unreadCount) => set({ notifications, unreadCount }),
  setNotifications: (notifications) => set({ notifications }),
  appendNotifications: (newNotifications) =>
    set((state) => ({
      notifications: [...state.notifications, ...newNotifications],
    })),
  addLive: (notification) =>
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + 1,
      lastLiveId: notification.id,
      toastNotification: notification,
    })),
  markRead: (id) =>
    set((state) => {
      const target = state.notifications.find((n) => n.id === id)
      if (!target || target.isRead) return state
      return {
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, isRead: true } : n,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }
    }),
  markAllRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
      unreadCount: 0,
    })),
  clearToast: () => set({ toastNotification: null }),
}))
